import type { ChangeEvent, KeyboardEvent, RefObject } from 'react';
import { Loader2, Paperclip, Send } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type {
  AgentSessionDTO,
  AssignedSkillDTO,
} from '@/services/roros/agent-sessions.service';
import { ModelTierPicker } from './ModelTierPicker';
import { SkillAutocomplete } from './AgentChatPageParts';

type ModelTierOverride = 'standard' | 'complex' | null;

interface AgentChatComposerProps {
  activeSession: AgentSessionDTO;
  modelTierOverride: ModelTierOverride;
  runningInputTokens: number;
  runningOutputTokens: number;
  runningCostUsd: number;
  onTierChanged: (tier: ModelTierOverride, resolvedModel: string | null) => void;
  uploadChipMsg: string | null;
  showAutocomplete: boolean;
  skills: AssignedSkillDTO[];
  skillQuery: string;
  onSelectSkill: (name: string) => void;
  uploadRef: RefObject<HTMLInputElement | null>;
  isUploading: boolean;
  onFileUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  inputText: string;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  isStreaming: boolean;
  onSendMessage: () => void;
}

export function AgentChatComposer({
  activeSession,
  modelTierOverride,
  runningInputTokens,
  runningOutputTokens,
  runningCostUsd,
  onTierChanged,
  uploadChipMsg,
  showAutocomplete,
  skills,
  skillQuery,
  onSelectSkill,
  uploadRef,
  isUploading,
  onFileUpload,
  inputRef,
  inputText,
  onInputChange,
  onInputKeyDown,
  isStreaming,
  onSendMessage,
}: AgentChatComposerProps) {
  const isLocked = activeSession.status === 'cancelled' || activeSession.status === 'completed';

  return (
    <div className="mt-2 border-t border-slate-200 pt-2 space-y-1.5">
      {/* AC-AGS-M4-7: Model tier picker (replaces static badge) */}
      <ModelTierPicker
        sessionId={activeSession.id}
        provider={activeSession.provider}
        model={activeSession.model}
        modelTierOverride={modelTierOverride}
        inputTokens={runningInputTokens}
        outputTokens={runningOutputTokens}
        costUsd={runningCostUsd}
        onTierChanged={onTierChanged}
      />

      {/* AC-M5-4: upload status chip */}
      {uploadChipMsg && (
        <p className="text-xs text-slate-500 px-1" data-testid="upload-chip">{uploadChipMsg}</p>
      )}

      {/* Input row with slash autocomplete (AC-AGS-6) + upload button (AC-M5-4) */}
      <div className="relative flex items-center gap-2">
        {showAutocomplete && (
          <SkillAutocomplete
            skills={skills}
            query={skillQuery}
            onSelect={onSelectSkill}
          />
        )}

        {/* AC-M5-4: hidden file input */}
        {/* eslint-disable-next-line roros/no-raw-html-in-routes -- reason: hidden file-picker input; no ui/* equivalent for type="file"; upload flow owned by M10e */}
        <input
          ref={uploadRef}
          type="file"
          className="hidden"
          accept=".pdf,.docx,.doc,.txt,.md,.csv,.jpg,.jpeg,.png,.gif,.webp"
          onChange={onFileUpload}
          data-testid="session-upload-input"
        />

        {/* AC-M5-4: paperclip button triggers file picker */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => uploadRef.current?.click()}
          disabled={isUploading || isLocked}
          aria-label={t('Upload file')}
          data-testid="session-upload-button"
          className="flex-shrink-0 text-slate-400 hover:text-slate-600"
        >
          {isUploading
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Paperclip className="h-4 w-4" />
          }
        </Button>

        <Input
          ref={inputRef}
          value={inputText}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          placeholder={t('Type a message… (type / for skills)')}
          disabled={isStreaming || isLocked}
          className="flex-1"
          data-testid="agent-chat-input"
        />

        <Button
          onClick={onSendMessage}
          disabled={!inputText.trim() || isStreaming || isLocked}
          size="sm"
          data-testid="agent-chat-send"
          aria-label={t('Send')}
        >
          {isStreaming
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Send className="h-4 w-4" />
          }
        </Button>
      </div>
    </div>
  );
}
